<script>
  import { copyTranslations } from "./copy.js";
  import Ranking from "./Ranking.svelte";
  import ExploreChart from "./ExploreChart.svelte";
  import { afterUpdate } from "svelte";
  import { fade } from "svelte/transition";
  import Select from "svelte-select";
  import { Shadow } from "svelte-loading-spinners";
  import { tweened } from "svelte/motion";
  import { cubicOut, cubicIn } from "svelte/easing";

  // Remove the jQuery/Chosen selects, display the native one
  afterUpdate(() => {
    window.$(".chosen-container").remove();
    window.$("select").css("display", "block");
  });

  // Detect the language from the url, to select the translated copy
  let path = window.location.pathname;
  let language;
  if (path.substr(path.length - 1, path.length) === "/") {
    language = path.substr(path.length - 3, 2);
  } else if (path.substr(path.length - 11, path.length) === "/index.html") {
    language = path.substr(path.length - 13, 2);
  }
  let copy = copyTranslations[language];

  // Defaults for the region/language selectors is Switzerland
  let selectedRegion = copy.languages.find((d) => d.value == "CH");
  let selectedExploreRegion = copy.languages.find((d) => d.value == "CH");

  // Initialise some values
  let selectedValue = undefined;
  let filterText = "";

  let top10Boys = [];
  let top10Girls = [];
  let top100 = [];
  let exploreNames = [];
  let highlightData = [];

  let disabled = false;

  function getSearchResults(list, search) {
    let resultsList = list
      .filter((item) =>
        item.label.toLowerCase().startsWith(search.toLowerCase())
      )
      .concat(
        list.filter(
          (item) =>
            item.label.toLowerCase().includes(filterText.toLowerCase()) &&
            !item.label.toLowerCase().startsWith(filterText.toLowerCase())
        )
      );
    if (resultsList.length === 0) {
      return [
        {
          label: `${copy.noresults} "${search}"`,
          value: `${copy.noresults} "${search}"`,
        },
      ];
    } else {
      return resultsList;
    }
  }

  $: filteredNames =
    filterText.length > 0
      ? getSearchResults(exploreNames, filterText)
      : exploreNames;
  $: if (
    filteredNames.length > 0 &&
    filteredNames[0].label.startsWith(copy.noresults)
  ) {
    disabled = true;
  } else {
    disabled = false;
  }

  let maxFrequency = tweened(1, {
    duration: 1000,
    easing: cubicIn,
  });
  let maxExploreFrequency = tweened(1, {
    duration: 1000,
    easing: cubicOut,
  });

  let exploreGender = "girls";

  let exploreChartW = 1;
  let exploreChartH = 1;

  let maxFreqBoys = 1;
  let maxFreqGirls = 1;

  // Data for the girls ranking
  let fetchJsonGirls = fetch(
    "../data/top10/girls/" + selectedRegion.value + ".json"
  )
    .then((res) => res.json())
    .then((data) => {
      top10Girls = data.data;
      maxFreqGirls = data.maxFrequency;
      maxFrequency.update(() => Math.max(maxFreqBoys, maxFreqGirls));
      top10Girls.sort((a, b) => a.rank - b.rank);
    });

  // Data for the boys ranking
  let fetchJsonBoys = fetch(
    "../data/top10/boys/" + selectedRegion.value + ".json"
  )
    .then((res) => res.json())
    .then((data) => {
      top10Boys = data.data;
      maxFreqBoys = data.maxFrequency;
      maxFrequency.update(() => Math.max(maxFreqBoys, maxFreqGirls));
      top10Boys.sort((a, b) => a.rank - b.rank);
    });

  // Data for the exploration chart
  let fetchTop100Girls = fetch(
    "../data/top100/girls/" + selectedExploreRegion.value + ".json"
  )
    .then((res) => res.json())
    .then((data) => {
      top100 = data.data;
      maxExploreFrequency.update(() => data.maxFrequency);
      top100.sort((a, b) => a.rank - b.rank);
    });

  // Data for the search box
  let languageNames = fetch(
    "../data/names/" +
      exploreGender +
      "/" +
      selectedExploreRegion.value +
      ".json"
  )
    .then((res) => res.json())
    .then((data) => {
      exploreNames = data.data.map((d) => {
        return {
          value: d,
          label: d,
        };
      });
    });

  // Function to update the rankings when a different region/language is selected
  function handleChange() {
    fetch("../data/top10/boys/" + selectedRegion.value + ".json")
      .then((res) => res.json())
      .then((data) => {
        top10Boys = data.data;
        maxFreqBoys = data.maxFrequency;
        maxFrequency.update(() => Math.max(maxFreqBoys, maxFreqGirls));
        top10Boys.sort((a, b) => a.rank - b.rank);
      });

    fetch("../data/top10/girls/" + selectedRegion.value + ".json")
      .then((res) => res.json())
      .then((data) => {
        top10Girls = data.data;
        maxFreqGirls = data.maxFrequency;
        maxFrequency.update(() => Math.max(maxFreqBoys, maxFreqGirls));
        top10Girls.sort((a, b) => a.rank - b.rank);
      });
  }

  // Function to update the Explore Chart
  function handleExploreChange() {
    highlightData = [];
    selectedValue = undefined;
    // Get new top 100 for the chart
    fetch(
      "../data/top100/" +
        exploreGender +
        "/" +
        selectedExploreRegion.value +
        ".json"
    )
      .then((res) => res.json())
      .then((data) => {
        top100 = data.data;
        maxExploreFrequency.update(() => data.maxFrequency);
        top100.sort((a, b) => a.rank - b.rank);
      });
    // Get new list of names
    fetch(
      "../data/names/" +
        exploreGender +
        "/" +
        selectedExploreRegion.value +
        ".json"
    )
      .then((res) => res.json())
      .then((data) => {
        exploreNames = data.data.map((d) => {
          return {
            value: d,
            label: d,
          };
        });
      });
  }

  // Function to keep the explore chart and the multiselect box in sync
  function handleSelect() {
    if (selectedValue !== undefined) {
      highlightData = [];
      selectedValue.forEach((d, i) => {
        // When the selected name is in the top100, just grab the data from there
        if (top100.find((nm) => nm.name === d.label)) {
          highlightData[i] = top100.find((nm) => nm.name === d.label);
        }
        // When the selected name is not in the top100, fetch its data
        else {
          const nameUrl =
            "../data/" +
            exploreGender +
            "/" +
            selectedExploreRegion.value +
            "/" +
            d.label +
            ".json";
          fetch(nameUrl)
            .then((res) => res.json())
            .then((data) => {
              highlightData[i] = {
                name: d.label,
                number: +data.number,
                data: data.data,
              };
            });
        }
      });
    } else {
      highlightData = [];
    }
  }

  $: disableSelect =
    selectedValue === undefined
      ? false
      : selectedValue.length < 6
      ? false
      : true;
</script>

<div class="intro">
  <h1>{copy.title}</h1>
  <p>{copy.intro}</p>
</div>

{#await fetchTop100Girls}
  <div class="spinner-overlay">
    <div class="spinner-container">
      <Shadow size="35" color="#333333" unit="px" duration="1s" />
    </div>
  </div>
{:then}
  <div class="app-content" in:fade={{ duration: 1000 }}>
    <h2>
      {copy.top10header}
      <select
        id="ranking-language-selector"
        bind:value={selectedRegion}
        on:change={handleChange}
      >
        {#each copy.languages as language}
          <option value={language}>
            {language.label}
          </option>
        {/each}
      </select>
    </h2>

    <p>{copy.top10explanation}</p>

    <div class="rankings-container">
      <div class="ranking girls-ranking-container">
        <h3 class="align-64">{copy.girls}</h3>
        {#await fetchJsonGirls}
          <Shadow size="20" color="#333333" unit="px" duration="1s" />
        {:then}
          <Ranking
            rankingData={top10Girls}
            {maxFrequency}
            lineColor={"#A332DE"}
            labels={true}
          />
        {/await}
      </div>
      <div class="ranking boys-ranking-container">
        <h3 class="align-64 boys">{copy.boys}</h3>
        {#await fetchJsonBoys}
          <Shadow size="20" color="#333333" unit="px" duration="1s" />
        {:then}
          <Ranking
            rankingData={top10Boys}
            {maxFrequency}
            lineColor={"#399D50"}
            labels={true}
          />
        {/await}
      </div>
    </div>

    <h2>{copy.exploreHeader}</h2>
    <p>{copy.exploreExplanation}</p>
    <div class="explore-controls">
      <div
        class={disableSelect
          ? "multiselect-container disabled"
          : "multiselect-container"}
      >
        {#await languageNames}
          <Shadow size="20" color="#333333" unit="px" duration="1s" />
        {:then}
          <Select
            items={filteredNames}
            isMulti={true}
            isSearchable={!disableSelect}
            bind:selectedValue
            bind:filterText
            isVirtualList={true}
            on:select={handleSelect}
            placeholder={copy.selectPlaceholder}
            hideEmptyState={false}
            noOptionsMessage={"No options match"}
            containerClasses={disabled ? "disabledItem" : ""}
          />
        {/await}
      </div>
      <div class="gender-toggle">
        <input
          id="girls"
          class="radio-button girls"
          type="radio"
          bind:group={exploreGender}
          value="girls"
          on:change={handleExploreChange}
        />
        <label for="girls"
          ><svg
            xmlns="http://www.w3.org/2000/svg"
            width="15.4"
            height="21.7"
            viewBox="0 0 22 31"
          >
            <g id="Female">
              <g
                id="Ellipse_1"
                data-name="Ellipse 1"
                fill="none"
                stroke={exploreGender === "girls"
                  ? "#fff"
                  : "rgb(118, 118, 118)"}
                stroke-width="3"
              >
                <circle cx="11" cy="11" r="11" stroke="none" />
                <circle cx="11" cy="11" r="9.5" fill="none" />
              </g>
              <line
                id="Line_2"
                data-name="Line 2"
                y1="8"
                transform="translate(11.5 21.5)"
                fill="none"
                stroke={exploreGender === "girls"
                  ? "#fff"
                  : "rgb(118, 118, 118)"}
                stroke-linecap="round"
                stroke-width="3"
              />
              <line
                id="Line_3"
                data-name="Line 3"
                y1="8"
                transform="translate(15.5 26.5) rotate(90)"
                fill="none"
                stroke={exploreGender === "girls"
                  ? "#fff"
                  : "rgb(118, 118, 118)"}
                stroke-linecap="round"
                stroke-width="3"
              />
            </g>
          </svg>
          {copy.girls}</label
        >

        <input
          id="boys"
          class="radio-button boys"
          type="radio"
          bind:group={exploreGender}
          value="boys"
          on:change={handleExploreChange}
        />
        <label for="boys"
          ><svg
            xmlns="http://www.w3.org/2000/svg"
            width="19"
            height="19"
            viewBox="0 0 26.45 26.45"
            id="male-icon"
          >
            <g id="Male">
              <g
                id="Ellipse_2"
                data-name="Ellipse 2"
                transform="translate(0 4.45)"
                fill="none"
                stroke={exploreGender === "boys"
                  ? "#fff"
                  : "rgb(118, 118, 118)"}
                stroke-width="3"
              >
                <circle cx="11" cy="11" r="11" stroke="none" />
                <circle cx="11" cy="11" r="9.5" fill="none" />
              </g>
              <line
                id="Line_4"
                data-name="Line 4"
                y1="8"
                transform="translate(24.328 2.122) rotate(45)"
                fill="none"
                stroke={exploreGender === "boys"
                  ? "#fff"
                  : "rgb(118, 118, 118)"}
                stroke-linecap="round"
                stroke-width="3"
              />
              <line
                id="Line_5"
                data-name="Line 5"
                x2="5.828"
                y2="0.172"
                transform="translate(18.5 1.95)"
                fill="none"
                stroke={exploreGender === "boys"
                  ? "#fff"
                  : "rgb(118, 118, 118)"}
                stroke-linecap="round"
                stroke-width="3"
              />
              <line
                id="Line_6"
                data-name="Line 6"
                x2="5.828"
                y2="0.172"
                transform="translate(24.5 2.122) rotate(90)"
                fill="none"
                stroke={exploreGender === "boys"
                  ? "#fff"
                  : "rgb(118, 118, 118)"}
                stroke-linecap="round"
                stroke-width="3"
              />
            </g>
          </svg>
          {copy.boys}</label
        >
      </div>
      <div class="explore-language-selector">
        <Select
          items={copy.languages}
          bind:selectedValue={selectedExploreRegion}
          on:select={handleExploreChange}
          isClearable={false}
          showIndicator={true}
        />
      </div>
    </div>
    <div
      class="explore-chart"
      bind:clientWidth={exploreChartW}
      bind:clientHeight={exploreChartH}
    >
      {#await fetchTop100Girls}
        <Shadow size="20" color="#333333" unit="px" duration="1s" />
      {:then}
        <ExploreChart
          width={exploreChartW}
          height={exploreChartH}
          exploreData={top100}
          {maxExploreFrequency}
          {highlightData}
          bind:selectedValue
          {exploreGender}
        />
      {/await}
    </div>
    <p class="note">{copy.note}</p>
    <p class="note">{copy.source}</p>
    <h2>{copy.linkheader}</h2>
    <p>{@html copy.link}</p>
  </div>
{/await}

<style>
  .spinner-overlay {
    width: 100%;
    height: 80vh;
    background-color: #ffffff;
  }
  .spinner-container {
    width: 100px;
    margin: auto;
    margin-top: 100px;
  }
  #ranking-language-selector {
    display: inline !important;
  }
  .rankings-container {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    margin-bottom: 70px;
  }
  .ranking {
    min-width: 400px;
  }
  h3.align-64 {
    padding-left: 74px;
    font-size: 2rem;
  }
  @media only screen and (max-width: 990px) {
    h3.align-64 {
      padding-left: 0px;
      margin-bottom: -12px;
    }
    h3.align-64.boys {
      margin-top: 50px;
    }
  }
  .ranking {
    flex: 1;
  }
  :global(.selectContainer) {
    border: 1px solid rgb(118, 118, 118) !important;
  }
  .gender-toggle {
    clear: both;
    display: inline-block;
    margin-bottom: 10px;
    margin-right: 10px;
  }
  .radio-button {
    display: none;
  }
  .radio-button + label {
    float: left;
    padding: 10px 1em;
    cursor: pointer;
    border: 1px solid rgb(118, 118, 118);
    margin-right: -1px;
    color: rgb(118, 118, 118);
    background-color: #fff;
    margin-bottom: 0px !important;
    height: 42px;
  }
  .radio-button + label:first-of-type {
    border-radius: 2px 0 0 2px;
  }
  .radio-button + label:last-of-type {
    border-radius: 0 2px 2px 0;
  }
  .radio-button.girls:checked + label {
    background-color: #a332de;
    color: #fff;
  }
  .radio-button.boys:checked + label {
    background-color: #399d50;
    color: #fff;
  }
  label svg {
    display: inline-block;
    vertical-align: middle;
    margin-right: 3px;
  }
  svg#male-icon {
    position: relative;
    bottom: 4px;
  }
  .explore-controls {
    display: flex;
    flex-wrap: wrap-reverse;
  }
  .multiselect-container {
    width: 500px;
    margin-right: 10px;
    --itemIsActiveBG: grey;
    --itemHoverBG: #eee;
  }
  @media only screen and (max-width: 992px) {
    .multiselect-container {
      width: 100%;
    }
  }
  .explore-chart {
    width: 100%;
    height: 550px;
  }
  .explore-language-selector {
    width: 220px;
    --itemIsActiveBG: grey;
    --itemHoverBG: #eee;
    margin-bottom: 12px;
  }
  .note {
    margin-top: 12px;
    font-size: 0.9em;
  }
  :global(.multiSelectItem:nth-of-type(6n + 1)) {
    background: #728cd7 !important;
    color: white;
  }
  :global(.multiSelectItem:nth-of-type(6n + 2)) {
    background: #4f25d2 !important;
    color: white;
  }
  :global(.multiSelectItem:nth-of-type(6n + 3)) {
    background: #cd35ab !important;
    color: white;
  }
  :global(.multiSelectItem:nth-of-type(6n + 4)) {
    background: #f56a00 !important;
    color: white;
  }
  :global(.multiSelectItem:nth-of-type(6n + 5)) {
    background: #dc0018 !important;
    color: white;
  }
  :global(.multiSelectItem:nth-of-type(6n)) {
    background: #00a2b9 !important;
    color: white;
  }
  :global(.disabled .listContainer) {
    visibility: hidden;
  }
  :global(.disabledItem .listItem) {
    pointer-events: none;
  }
</style>
